// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

/**
 * Per-column filter for the report table. For ANY column of ANY report (server mode), it shows a
 * searchable, scrollable, multi-select CHECKBOX list of that column's distinct values — the values
 * come from the report's own SQL and the search hits the server, so even a high-cardinality column
 * (thousands of drugs) works: type to narrow, tick the ones you want. It only stages the selection;
 * the parent's Apply runs the query. In client mode (no distinct source) it degrades to a text box.
 *
 * The panel is position:fixed (anchored to the trigger) so the table's horizontal-scroll container
 * can't clip it, with a transparent backdrop to click away.
 *
 * Gate 19: no native dropdown element — only text inputs, a search input, and checkboxes.
 */
import { useEffect, useRef, useState } from 'react'

export interface ColumnValueFilterProps {
  col: string
  text: string
  values: string[]
  onText: (v: string) => void
  onValues: (vals: string[]) => void
  onEnter?: () => void
  fetchDistinct?: (col: string, search?: string) => Promise<{ values: string[]; too_many: boolean }>
}

export default function ColumnValueFilter({ col, text, values, onText, onValues, onEnter, fetchDistinct }: ColumnValueFilterProps) {
  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ top: number; left: number; width: number } | null>(null)
  const [search, setSearch] = useState('')
  const [list, setList] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [tooMany, setTooMany] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)

  // Load distinct values (server-filtered by the search term), debounced, whenever the panel is open.
  useEffect(() => {
    if (!open || !fetchDistinct) return
    let cancelled = false
    clearTimeout(timer.current)
    setLoading(true)
    timer.current = setTimeout(async () => {
      try {
        const r = await fetchDistinct(col, search.trim() || undefined)
        if (!cancelled) { setList(r.values); setTooMany(r.too_many) }
      } catch { if (!cancelled) { setList([]); setTooMany(false) } }
      finally { if (!cancelled) setLoading(false) }
    }, 200)
    return () => { cancelled = true; clearTimeout(timer.current) }
  }, [open, search, col, fetchDistinct])

  function openPanel() {
    const r = btnRef.current?.getBoundingClientRect()
    if (r) setCoords({ top: r.bottom + 3, left: r.left, width: r.width })
    setOpen(true)
  }
  function close() { setOpen(false); onEnter?.() }

  const selected = new Set(values)
  const label = values.length > 0 ? `${values.length} selected` : (text.trim() ? text.trim() : 'filter…')
  const active = values.length > 0 || text.trim() !== ''

  function toggle(v: string) {
    const next = new Set(selected)
    if (next.has(v)) next.delete(v); else next.add(v)
    onValues([...next])
  }
  function selectAllShown() {
    const next = new Set(selected)
    for (const v of list) next.add(v)
    onValues([...next])
  }

  return (
    <>
      <button ref={btnRef} type="button" onClick={() => (open ? setOpen(false) : openPanel())}
        aria-label={`Filter ${col}`} title={active ? label : `Filter ${col}`}
        style={{
          width: '100%', minWidth: 70, padding: '3px 6px', textAlign: 'left', cursor: 'pointer',
          border: `1px solid ${active ? 'var(--color-primary, #0d9488)' : 'var(--color-border)'}`, borderRadius: 5,
          font: 'inherit', fontSize: 11.5, background: 'var(--color-surface)',
          color: active ? 'var(--color-text)' : 'var(--color-text-mute)',
          whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
        {label}
      </button>

      {open && coords && (
        <>
          <div onClick={close} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
          <div style={{
            position: 'fixed', zIndex: 41, top: coords.top, left: coords.left, minWidth: Math.max(220, coords.width), maxWidth: 340,
            background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 7,
            boxShadow: '0 8px 24px rgba(0,0,0,0.16)', padding: 8,
          }}>
            {fetchDistinct ? (
              <div>
                <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
                  placeholder="search values…" aria-label={`Search ${col} values`} style={inputStyle} />
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', margin: '6px 0 4px', fontSize: 10.5 }}>
                  <span style={{ color: 'var(--color-text-mute)' }}>{values.length} selected</span>
                  <span style={{ display: 'flex', gap: 8 }}>
                    {list.length > 0 && <button type="button" onClick={selectAllShown} style={linkBtn}>Select all shown</button>}
                    {values.length > 0 && <button type="button" onClick={() => onValues([])} style={linkBtn}>Clear</button>}
                  </span>
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {loading && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', padding: '4px 2px' }}>Loading…</div>}
                  {!loading && list.length === 0 && <div style={{ fontSize: 11.5, color: 'var(--color-text-mute)', padding: '4px 2px' }}>No matching values.</div>}
                  {!loading && list.map(v => (
                    <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 2px', fontSize: 12, cursor: 'pointer' }}>
                      <input type="checkbox" checked={selected.has(v)} onChange={() => toggle(v)} />
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={v}>{v || '(blank)'}</span>
                    </label>
                  ))}
                </div>
                {tooMany && <div style={{ fontSize: 10.5, color: 'var(--color-text-mute)', marginTop: 4 }}>Showing first 200 — keep typing to narrow.</div>}
              </div>
            ) : (
              <input autoFocus value={text} onChange={e => onText(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') close() }}
                placeholder="contains…" aria-label={`Filter ${col} text`} style={inputStyle} />
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6, marginTop: 8 }}>
              <button type="button" onClick={close} style={applyBtn}>Apply</button>
            </div>
          </div>
        </>
      )}
    </>
  )
}

const inputStyle: React.CSSProperties = {
  width: '100%', padding: '4px 6px', border: '1px solid var(--color-border)', borderRadius: 5,
  font: 'inherit', fontSize: 12, background: 'var(--color-surface)', boxSizing: 'border-box',
}
const linkBtn: React.CSSProperties = { border: 'none', background: 'none', color: 'var(--color-primary, #0d9488)', cursor: 'pointer', fontSize: 10.5, padding: 0 }
const applyBtn: React.CSSProperties = { padding: '4px 12px', border: 'none', borderRadius: 6, background: 'var(--color-primary, #0d9488)', color: '#fff', fontSize: 11.5, fontWeight: 600, cursor: 'pointer' }
